import classNames from 'classnames';
import type { CSSProperties, HTMLAttributes, ReactElement } from 'react';

import TableCell from './table-cell';
import type { ReactTableCell, ReactTableRow, ReactTableState } from './types';

export interface TableRowProps<T extends object> {
  /**
   * Rendered beneath the row when it is expanded.
   */
  SubComponent?: (row: ReactTableRow<T>) => ReactElement;
  /**
   * Resolves the fixed-width style for a cell.
   */
  getCellStyle: (cell: ReactTableCell<T>) => CSSProperties;
  /**
   * Extension point for per-cell DOM props.
   */
  getTdProps: (
    state: ReactTableState<T>,
    cell: ReactTableCell<T>,
  ) => HTMLAttributes<HTMLTableCellElement>;
  /**
   * Extension point for per-row DOM props.
   */
  getTrProps: (
    state: ReactTableState<T>,
    row: ReactTableRow<T>,
  ) => HTMLAttributes<HTMLTableRowElement>;
  /**
   * Prepares the row before its cells are read (required by react-table).
   */
  prepareRow: (row: ReactTableRow<T>) => void;
  /**
   * The row to render.
   */
  row: ReactTableRow<T>;
  /**
   * The row's position, used for odd/even striping.
   */
  rowIndex: number;
  /**
   * The current table state.
   */
  state: ReactTableState<T>;
}

/**
 * Renders a body row (`rt-tr`) with odd/even striping and any expanded
 * SubComponent, delegating each cell to TableCell.
 *
 * @deprecated Internal to the legacy `ReactTable`; superseded by the table
 * primitives in `@components/table`.
 */
const TableRow = <T extends object>({
  SubComponent,
  getCellStyle,
  getTdProps,
  getTrProps,
  prepareRow,
  row,
  rowIndex,
  state,
}: TableRowProps<T>) => {
  prepareRow(row);

  const trProps = getTrProps(state, row);
  const { key: rowKey, ...rowProps } = row.getRowProps();

  return (
    <>
      <div
        key={rowKey}
        {...rowProps}
        {...trProps}
        className={classNames(
          'rt-tr',
          rowIndex % 2 === 0 ? '-odd' : '-even',
          trProps.className,
        )}
      >
        {row.cells.map(cell => {
          const { key: cellKey, ...cellProps } = cell.getCellProps({
            className: classNames('rt-td', cell.column.className),
            style: {
              ...getCellStyle(cell),
              ...cell.column.style,
            },
          });

          return (
            <TableCell
              key={cellKey}
              cell={cell}
              cellProps={cellProps}
              getTdProps={getTdProps}
              state={state}
            />
          );
        })}
      </div>

      {row.isExpanded && SubComponent ? SubComponent(row) : null}
    </>
  );
};

export default TableRow;
